import { useMemo } from 'react'

import type { WorkflowCase } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'

import { useGetWorkflowCase } from '~/hooks/workflowCase/useGetWorkflowCase'
import { useWorkflowCaseIdParam } from '~/hooks/workflowCase/useWorkflowCaseIdParam'
import { fromProtoDate } from '~/models/dateTime'
import { isWorkflowCaseNotFoundError } from '~/models/workflowCaseNotFound'
import { isWorkflowCaseReadonly } from '~/models/workflowCaseReadonly'

type CurrentWorkflowCase = {
  /**
   * URL クエリパラメータ `?workflowId=` (旧 `?caseId=` も受け付ける) から解決した
   * ワークフロー ID (無ければ undefined)。
   */
  caseId: string | undefined
  /** caseId に対応するワークフローケース (未取得 / caseId 無しの間は undefined)。 */
  wfCase: WorkflowCase | undefined
  /**
   * ワークフロー起点で開いている画面の read-only 判定 (fail-closed)。
   *
   * caseId が無い (= ワークフロー非連動の通常利用) 間は false。caseId が付いて
   * いる間は、取得完了 (`wfCase` 確定) までは read-only 側に倒し
   * (fail-closed)、確定後は `isWorkflowCaseReadonly` の判定に従う。
   *
   * fail-closed にする理由 (旧 SpotBidding 実装のコメント準拠): ケース状態が
   * 確定しないまま書込導線を開くと、完了済み / 削除済みケースを開いた直後の
   * 応答遅延中に、承認申請・削除・全クリア等の書込導線が一瞬だけ開く window が
   * できる。取得失敗時も同様に read-only へ倒す (= 本 hook は
   * `throwOnError: false` で取得し、失敗時も画面の描画自体は続ける。失敗を
   * 画面上でハンドリングしたい呼び出し側は別途 `useGetWorkflowCase` を購読する
   * — cache key ( `['workflowCase', caseId]` 相当) を共有するため、fetch 自体は
   * 重複しない)。
   */
  isReadonly: boolean
  /**
   * `?workflowId=` 経由で開いているか (= 対象日操作 UI を disabled にすべきか)。
   * `caseId !== undefined` と同値 (取得完了前から true、fail-closed。
   * AC-WFW-006a Decision 1)。
   */
  isDateLocked: boolean
  /**
   * `isDateLocked` が true のときにケースが持つ対象日 (取得完了後のみ確定)。
   * caseId 無し、または caseId 付きでも `wfCase` 未取得 (取得中 / 取得失敗) の
   * 間は undefined — 呼び出し側は画面既定値へフォールバックせず、この値が
   * `undefined` の間は対象日依存のデータ取得を開始しないこと (fail-closed、
   * AC-WFW-006a Decision 1)。
   */
  lockedTargetDate: Date | undefined
  /**
   * 対象日依存のデータ取得を開始してよいか (AC-WFW-006a Decision 1)。
   * `isDateLocked` と `lockedTargetDate` の組から呼び出し側が誤読しないよう、
   * 1 概念に集約したもの (`!isDateLocked || lockedTargetDate !== undefined` と
   * 同値)。3 状態:
   *
   * - **未固定** (`isDateLocked=false`、caseId 無し): 常に true。画面既定値 /
   *   永続 state / `?date=` をそのまま使ってよい
   * - **固定・未確定** (`isDateLocked=true` かつ `lockedTargetDate=undefined`、
   *   caseId 付きで wfCase 取得中 / 取得失敗): false。対象日依存の query を
   *   `enabled: isTargetDateReady` 等で止め、画面既定日にフォールバックしない
   * - **固定・確定** (`isDateLocked=true` かつ `lockedTargetDate` 確定): true。
   *   `lockedTargetDate` を対象日として対象日依存の query を発火してよい
   */
  isTargetDateReady: boolean
  /**
   * workflowId のワークフローが見つからない (存在しない・他組織・形式不正) か
   * (spec AC-WFW-040)。true の間、`WorkflowPageLayout` が画面本体を
   * `WorkflowNotFoundView` に差し替える (対象日も決まらないので `isTargetDateReady` は
   * false のまま、`isReadonly` は true のまま)。遷移先画面は `WorkflowPageLayout` を
   * 使えばよく、本値を見るのは同 layout を通らない早期 return を持つ画面だけ。通信エラー・サーバーエラーでは
   * false (`isWorkflowCaseNotFoundError` の docstring)。
   */
  isWorkflowNotFound: boolean
  /**
   * 固定中だが対象日を得られない (取得が通信エラー・サーバーエラーで失敗した、または
   * ケースが対象日を持たない) か。true の間 `isTargetDateReady` は false のまま
   * 戻らないので、対象日依存の query を止めている画面は「0 件」 ではなく画面の
   * エラー表示を出す。失敗後の再取得中も true のまま (React Query は一度も成功して
   * いない query の再取得開始時に error を null に戻すため、失敗の有無を error ではなく
   * 失敗時刻で見る)。初回の取得中・caseId 無し・見つからない (`isWorkflowNotFound`) では
   * false。
   */
  isTargetDateUnavailable: boolean
  /**
   * ケースを取り直す (`isTargetDateUnavailable` の画面で再読み込みを出す時用)。本 hook と
   * 同じ query (`throwOnError: false`) を refetch するので、呼び出し側で
   * `useGetWorkflowCase` を購読し直して throwOnError を揃える必要はない。
   */
  refetchWorkflowCase: () => void
  /** ケースを取得中か (初回取得・`refetchWorkflowCase` の再取得を含む)。 */
  isWorkflowCaseFetching: boolean
}

/**
 * URL クエリパラメータ `?workflowId=` からワークフローケースを解決する共通 hook。
 *
 * 16+ ページに写経されていた「caseId 解決 → `useGetWorkflowCase` →
 * `isWorkflowCaseReadonly` 判定」の 6 行レシピを集約する。
 *
 * 需要想定(日次編集)画面等、`isWorkflowCaseReadonly` とは異なる read-only
 * 判定 (`isDemandForecastReadonly` 等) が要る画面は、本 hook から `wfCase` だけ
 * 受け取り、`isReadonly` は使わず自前の判定式を適用する。
 */
export function useCurrentWorkflowCase(): CurrentWorkflowCase {
  const caseId = useWorkflowCaseIdParam()
  const {
    data: wfCaseData,
    error: wfCaseError,
    errorUpdatedAt: wfCaseErrorUpdatedAt,
    refetch: refetchWfCase,
    isFetching: isWorkflowCaseFetching,
  } = useGetWorkflowCase(caseId, {
    throwOnError: false,
  })
  const wfCase = wfCaseData?.workflowCase
  const isWorkflowNotFound =
    caseId !== undefined && isWorkflowCaseNotFoundError(wfCaseError)
  const isReadonly =
    caseId !== undefined &&
    (wfCase === undefined || isWorkflowCaseReadonly(wfCase))
  const isDateLocked = caseId !== undefined
  // `fromProtoDate` は毎回新しい `Date` を返すため、 dep (proto の year/month/day)
  // で memo 化して参照を安定させる。 これが無いと consumer の
  // `useEffect`/`useMemo` (dep に targetDate を含むもの) が毎 render 無効化され、
  // 例えばスポット入札の受渡日変更 effect が毎 render 発火して一覧絞り込みが
  // 即解除される (OzawaShigenori review 指摘、 caseId 経路限定の退行)。
  const caseProtoTargetDate = wfCase?.targetDate
  const lockedTargetDate = useMemo(
    () =>
      caseProtoTargetDate !== undefined
        ? fromProtoDate(caseProtoTargetDate)
        : undefined,
    [caseProtoTargetDate],
  )
  const isTargetDateReady = !isDateLocked || lockedTargetDate !== undefined
  const isTargetDateUnavailable =
    isDateLocked &&
    !isWorkflowNotFound &&
    (wfCase !== undefined
      ? lockedTargetDate === undefined
      : wfCaseErrorUpdatedAt > 0)

  return {
    caseId,
    wfCase,
    isReadonly,
    isDateLocked,
    lockedTargetDate,
    isTargetDateReady,
    isWorkflowNotFound,
    isTargetDateUnavailable,
    refetchWorkflowCase: () => {
      void refetchWfCase()
    },
    isWorkflowCaseFetching,
  }
}
